/**
 * TopicTrendChart
 * Stacked area chart for auto-topic tag frequency over time.
 * Recharts AreaChart with gradient fills, top-N tags + "other",
 * a top-N selector, a "% share" toggle, and a breakdown table.
 */

import { useState, useMemo } from 'react';
import { activeLocale } from '@/utils/locale';
import { useLanguage } from '@/hooks/useLanguage';
import { Switch, Typography, Empty, Spin, Tag, Table } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TagTimeseries } from '../types/insights';
import { useChartTheme, tooltipStyle as makeTooltipStyle, axisTickStyle, gridProps } from '../utils/chartTheme';
import type { TFunc } from '@/utils/tfunc';

const { Text } = Typography;

const PALETTE = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
  '#06b6d4',
  '#84cc16',
  '#f97316',
  '#ec4899',
  '#64748b',
];
const OTHER_COLOR = '#94a3b8';

function formatBucket(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString(activeLocale(), { month: 'short', day: 'numeric' });
}

function tagColor(tag: string, index: number): string {
  if (tag === '__other__') return OTHER_COLOR;
  return PALETTE[index % PALETTE.length];
}

function tagLabel(t: TFunc, tag: string): string {
  if (tag === '__other__') return t('insights.otherTopics');
  return tag;
}

// ─── Breakdown table ──────────────────────────────────────────────────────────

interface BreakdownRow {
  tag: string;
  total: number;
  first: number;
  second: number;
  trend: number | null; // % change first→second
  color: string;
}

function TopicsBreakdownTable({ data, distribution, onTopicClick }: { data: TagTimeseries; distribution?: Record<string, number>; onTopicClick?: (tag: string) => void }) {
  const { t } = useLanguage();
  const ct = useChartTheme();
  const rows: BreakdownRow[] = useMemo(() => {
    if (!data.series.length || !data.buckets.length) return [];

    const mid = Math.floor(data.buckets.length / 2);

    return data.series
      .filter(s => s.tag !== '__other__')
      .map((s, i) => {
        const first  = s.counts.slice(0, mid).reduce((a, b) => a + b, 0);
        const second = s.counts.slice(mid).reduce((a, b) => a + b, 0);
        // Use distribution counts (accurate totals) when available, fall back to timeseries sums
        const total  = distribution?.[s.tag] ?? s.counts.reduce((a, b) => a + b, 0);
        const trend  = first > 0 ? ((second - first) / first) * 100 : null;
        return { tag: s.tag, total, first, second, trend, color: tagColor(s.tag, i) };
      })
      .sort((a, b) => b.total - a.total);
  }, [data, distribution]);

  if (rows.length === 0) return null;

  const maxTotal = Math.max(...rows.map(r => r.total), 1);
  const grandTotal = rows.reduce((s, r) => s + r.total, 0);

  const columns = [
    {
      title: '#',
      key: 'rank',
      width: 32,
      align: 'center' as const,
      render: (_: any, __: any, i: number) => (
        <Text style={{ fontSize: 11, color: ct.mutedText, fontWeight: 600 }}>{i + 1}</Text>
      ),
    },
    {
      title: t('insights.topic'),
      dataIndex: 'tag',
      key: 'tag',
      width: 140,
      align: 'center' as const,
      ellipsis: true,
      render: (tag: string, row: BreakdownRow) => (
        <Tag
          style={{
            background: row.color + '18',
            borderColor: row.color + '55',
            color: ct.secondaryText,
            fontSize: 11,
            borderRadius: 10,
            padding: '0 7px',
            margin: 0,
            cursor: onTopicClick ? 'pointer' : 'default',
            transition: 'all 0.15s ease',
          }}
          onClick={() => onTopicClick?.(tag)}
          onMouseEnter={(e) => {
            if (onTopicClick) {
              e.currentTarget.style.borderColor = row.color;
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = row.color + '55';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {tag}
        </Tag>
      ),
    },
    {
      title: t('insights.count'),
      dataIndex: 'total',
      key: 'total',
      width: 180,
      align: 'center' as const,
      render: (total: number, row: BreakdownRow) => {
        const barPct = Math.round((total / maxTotal) * 100);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, position: 'relative', height: 18, background: ct.barTrackBg, borderRadius: 4 }}>
              <div
                style={{
                  position: 'absolute',
                  insetInlineStart: 0,
                  top: 0,
                  bottom: 0,
                  width: `${barPct}%`,
                  background: row.color,
                  borderRadius: 4,
                  transition: 'width 0.4s ease',
                  minWidth: total > 0 ? 4 : 0,
                }}
              />
            </div>
            <Text style={{ fontSize: 11, fontWeight: 600, width: 36, textAlign: 'end', flexShrink: 0 }}>{total}</Text>
          </div>
        );
      },
    },
    {
      title: '%',
      dataIndex: 'total',
      key: 'pct',
      width: 56,
      align: 'center' as const,
      render: (total: number) => {
        const pct = grandTotal > 0 ? (total / grandTotal) * 100 : 0;
        return <Text style={{ fontSize: 11, fontWeight: 500, color: ct.mutedText }}>{pct.toFixed(1)}%</Text>;
      },
    },
    {
      title: t('insights.agentStats.trend'),
      dataIndex: 'trend',
      key: 'trend',
      width: 76,
      align: 'center' as const,
      render: (pct: number | null) => {
        if (pct === null) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        const up = pct >= 0;
        return (
          <span style={{ color: up ? ct.positiveText : ct.negativeText, fontWeight: 500, fontSize: 11 }}>
            {up
              ? <ArrowUpOutlined style={{ fontSize: 10 }} />
              : <ArrowDownOutlined style={{ fontSize: 10 }} />
            }{' '}{Math.abs(pct).toFixed(0)}%
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Text style={{ fontSize: 11, fontWeight: 700, color: ct.mutedText, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 2 }}>
        {t('insights.topNTopics', { count: rows.length })}
      </Text>
      {onTopicClick && (
        <Text style={{ fontSize: 11, color: ct.mutedText, display: 'block', marginBottom: 6 }}>
          {t('insights.clickOnATopicTo')}
        </Text>
      )}
      <Table
        columns={columns}
        dataSource={rows.map(r => ({ ...r, key: r.tag }))}
        pagination={false}
        size="small"
        style={{ borderRadius: 8 }}
      />
    </div>
  );
}

// ─── Main chart ───────────────────────────────────────────────────────────────

interface TopicTrendChartProps {
  data: TagTimeseries | null;
  loading: boolean;
  error?: string | null;
  large?: boolean;
  /** Distribution counts for accurate totals in the breakdown table */
  distribution?: Record<string, number>;
  /** Called when user clicks a topic in the breakdown table */
  onTopicClick?: (tag: string) => void;
}

export function TopicTrendChart({
  data,
  loading,
  error,
  large = false,
  distribution,
  onTopicClick,
}: TopicTrendChartProps) {
  const { t } = useLanguage();
  const ct = useChartTheme();
  const [normalized, setNormalized] = useState(false);
  const [hiddenTags, setHiddenTags] = useState<Set<string>>(new Set());

  const chartData = useMemo(() => {
    if (!data?.buckets?.length) return [];

    return data.buckets.map((bucket, bi) => {
      const point: Record<string, string | number> = { bucket: formatBucket(bucket) };

      const rowTotal = normalized
        ? data.series.reduce((sum, s) => sum + (s.counts[bi] ?? 0), 0)
        : 1;

      for (const s of data.series) {
        const raw = s.counts[bi] ?? 0;
        point[s.tag] = normalized && rowTotal > 0 ? Math.round((raw / rowTotal) * 100) : raw;
      }

      return point;
    });
  }, [data, normalized]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Text type="danger">{error}</Text>
      </div>
    );
  }

  if (!data || data.buckets.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<Text type="secondary" style={{ fontSize: 13 }}>{t('insights.noTrendData')}</Text>}
        style={{ padding: '32px 0' }}
      />
    );
  }

  const chartH = large ? 320 : 240;
  const tags = data.series.map(s => s.tag);
  const visibleTopics = tags.filter(t => t !== '__other__').length;

  // Build gradient defs
  const gradients = tags.map((tag, i) => {
    const color = tagColor(tag, i);
    return { id: `grad-${i}`, color };
  });

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1 }}>
          {tags.slice(0, 6).map((tag, i) => (
            <Tag
              key={tag}
              style={{
                background: tagColor(tag, i) + '20',
                borderColor: tagColor(tag, i) + '60',
                color: tag === '__other__' ? ct.mutedText : ct.secondaryText,
                fontSize: 11,
                borderRadius: 12,
                padding: '1px 8px',
              }}
            >
              {tagLabel(t, tag)}
            </Tag>
          ))}
          {tags.length > 6 && (
            <Tag style={{ fontSize: 11, borderRadius: 12, padding: '1px 8px', color: ct.mutedText }}>
              +{tags.length - 6} more
            </Tag>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <Text style={{ fontSize: 12, color: ct.mutedText }}>% share</Text>
          <Switch size="small" checked={normalized} onChange={setNormalized} />
        </div>
      </div>

      {/* Chart */}
      <svg width={0} height={0}>
        <defs>
          {gradients.map(g => (
            <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={g.color} stopOpacity={0.35} />
              <stop offset="95%" stopColor={g.color} stopOpacity={0.05} />
            </linearGradient>
          ))}
        </defs>
      </svg>

      <ResponsiveContainer width="100%" height={chartH}>
        <AreaChart data={chartData} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid {...gridProps(ct)} />
          <XAxis
            dataKey="bucket"
            tick={axisTickStyle(ct)}
            tickLine={false}
            axisLine={{ stroke: ct.xAxisLine }}
          />
          <YAxis
            tick={axisTickStyle(ct)}
            tickLine={false}
            axisLine={false}
            tickFormatter={normalized ? (v: number) => `${v}%` : undefined}
          />
          <Tooltip
            contentStyle={makeTooltipStyle(ct)}
            formatter={(value: number, name: string) => [
              normalized ? `${value}%` : value,
              tagLabel(t, name),
            ]}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, paddingTop: 12, cursor: 'pointer' }}
            onClick={(e: any) => {
              const key = e.dataKey ?? e.value;
              setHiddenTags(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key); else next.add(key);
                return next;
              });
            }}
            formatter={(value: string) => (
              <span style={{ color: hiddenTags.has(value) ? ct.legendHidden : undefined, textDecoration: hiddenTags.has(value) ? 'line-through' : undefined }}>
                {tagLabel(t, value)}
              </span>
            )}
          />
          {tags.map((tag, i) => (
            <Area
              key={tag}
              type="monotone"
              dataKey={tag}
              name={tag}
              stackId="1"
              stroke={tagColor(tag, i)}
              strokeWidth={1.5}
              fill={`url(#grad-${i})`}
              fillOpacity={1}
              hide={hiddenTags.has(tag)}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>

      <div style={{ marginTop: 8, textAlign: 'end' }}>
        <Text style={{ fontSize: 11, color: ct.mutedText }}>
          {t('insights.bucketedBy', { unit: data.bucket_unit ?? 'week' })} · {t('counts.topics', { count: visibleTopics })}{t('insights.shownSuffix')}
        </Text>
      </div>

      {/* Topics breakdown table */}
      <TopicsBreakdownTable data={data} distribution={distribution} onTopicClick={onTopicClick} />
    </div>
  );
}
